import { useState } from "react";

const SOFTWARE_RENDERER_PATTERN =
  /swiftshader|llvmpipe|softpipe|software rasterizer|basic render/i;

let cachedHardwareAcceleration: boolean | undefined;

// Normally already answered by the same check inline in index.html (keep the
// two in sync); this covers pages without it.
function detectHardwareAcceleration() {
  if (typeof document === "undefined") return false;
  if (new URLSearchParams(window.location.search).get("graphics") === "lite") {
    return false;
  }

  const canvas = document.createElement("canvas");
  const context =
    canvas.getContext("webgl2", {
      powerPreference: "high-performance",
      failIfMajorPerformanceCaveat: true,
    }) ??
    canvas.getContext("webgl", {
      powerPreference: "high-performance",
      failIfMajorPerformanceCaveat: true,
    });

  if (!context) return false;

  const debugInfo = context.getExtension("WEBGL_debug_renderer_info");
  const renderer = String(
    debugInfo
      ? context.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      : context.getParameter(context.RENDERER)
  );
  const loseContext = context.getExtension("WEBGL_lose_context");

  loseContext?.loseContext();

  return !SOFTWARE_RENDERER_PATTERN.test(renderer);
}

declare global {
  interface Window {
    /** Set by the inline check at the end of index.html. */
    __hardwareAcceleration?: boolean;
  }
}

export function useHardwareAcceleration() {
  const [isHardwareAccelerated] = useState(() => {
    if (cachedHardwareAcceleration === undefined) {
      cachedHardwareAcceleration =
        window.__hardwareAcceleration ?? detectHardwareAcceleration();
    }

    return cachedHardwareAcceleration;
  });

  return isHardwareAccelerated;
}
